import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';
import { Sheet } from '@/nova/components/Sheet';
import { SuggestionsHeader } from '../../components/SuggestionsHeader';
import { NameCard } from '../../components/NameCard';
import { DomainRow, DOMAIN_ROW_HEIGHT } from '../../components/DomainRow';
import { SuggestionPager } from '../../components/SuggestionPager';
import { Skeleton } from '../../components/Skeleton';
import { BrandNameInput } from '../../components/BrandNameInput';
import { PencilIcon, RefreshIcon, BoltIcon, LinkIcon } from '../../components/icons';
import { useCustomizer } from '../../store/customizer';
import { DesktopConnect, MobileConnect } from './connect';
import { formatDomainPrice } from '../../lib/currency';
import type { DomainSuggestion, NameSuggestion } from '../../lib/types';
import {
  DesktopBody,
  Section,
  NameGrid,
  DomainList,
  ModalBodyText,
  MobileBody,
  MobileHeading,
  MobileSubtext,
  MobileRefresh,
  MobileRefreshLabel,
  MobileList,
  DecideFastBtn,
  DecideFastLabel,
  ConnectRow,
  ConnectInfo,
  ConnectBtn,
  ConnectBtnLabel,
} from './brand.styles';

const SKELETON_KEYS = ['s1', 's2', 's3'];

const NameCardsView = ({
  suggestions,
  selectedName,
  onSelect,
  onMore,
}: {
  suggestions: NameSuggestion[];
  selectedName: string | null;
  onSelect: (name: string) => void;
  /** Desktop only: clamps each card to two lines + a "More" reveal. Omit on
   *  mobile, where the cards show the full rationale in a scrollable sub-step. */
  onMore?: (suggestion: NameSuggestion) => void;
}): React.ReactElement => (
  <>
    {suggestions.map((s) => (
      <NameCard
        key={s.name}
        name={s.name}
        description={s.why_it_works}
        selected={s.name === selectedName}
        onSelect={() => onSelect(s.name)}
        onMore={onMore ? () => onMore(s) : undefined}
      />
    ))}
  </>
);

const DomainRowsView = ({
  domains,
  selectedDomain,
  onSelect,
}: {
  domains: DomainSuggestion[];
  selectedDomain: string | null;
  onSelect: (domain: string) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  // `formatDomainPrice` lives in a shared lib and returns categorical chrome that
  // must translate: the "Free" word and the "/year" unit suffix. The numeric price
  // + currency symbol is sample data and stays as-is; only the chrome is localized
  // here at the render site (the lib keeps its stable English output).
  const priceLabel = (label: string, free: boolean): string => {
    if (free) return t('Free');
    const price = label.replace(/\/year$/, '');
    return t('{{price}}/year', { price });
  };
  return (
    <>
      {domains.map((d) => {
        const { label, free } = formatDomainPrice(d.price, d.currency);
        return (
          <DomainRow
            key={d.domain}
            domain={d.domain}
            priceLabel={priceLabel(label, free)}
            free={free}
            selected={d.domain === selectedDomain}
            onSelect={() => onSelect(d.domain)}
          />
        );
      })}
    </>
  );
};

/** "I have my own domain" → opens the connect-domain sub-flow. */
const ConnectDomainRow = ({ onConnect }: { onConnect: () => void }): React.ReactElement => (
  <ConnectRow>
    <ConnectInfo text="I have my own domain" tag="span" />
    <ConnectBtn type="button" onClick={onConnect}>
      <LinkIcon size={16} />
      <ConnectBtnLabel text="Connect domain" tag="span" />
    </ConnectBtn>
  </ConnectRow>
);

/**
 * Step 2 — Name & Domain. AI subflow (name cards → domains) and the manual
 * subflow ("I have my own name": an underline input whose typed name is
 * debounced into a domain lookup). Desktop shows it on one screen; mobile splits
 * into sub-steps (1a/1b AI, 2a/2b manual). The manual domains come from the
 * godaddy `suggest` API; AI names+domains from one region API.
 */
export const BrandStep = (): React.ReactElement => {
  const { t } = useTranslation();
  // Desktop "More" reveal: which suggestion's full name + rationale to show in the
  // modal (null = closed).
  const [moreSuggestion, setMoreSuggestion] = React.useState<NameSuggestion | null>(null);
  const {
    isMobile,
    step2Sub,
    names,
    loadingNames,
    domains,
    domainsPaging,
    domainsNav,
    selectedName,
    selectedDomain,
    setName,
    setDomain,
    refreshNames,
    refreshDomains,
    country,
    manualMode,
    setManualMode,
    typedName,
    setTypedName,
    loadingManualDomains,
    connectStage,
    openConnectDomain,
  } = useCustomizer();

  const countryName = country?.name ?? '';
  // Skeleton only while a fetch is actually in flight. An EMPTY result set is NOT
  // "loading": the godaddy `suggest` API can legitimately return zero domains, and
  // `|| domains.length === 0` would then spin the skeleton forever, trapping the
  // user on step 2 (with an empty list they can still pick "Connect domain").
  // During the 500ms type-debounce we show the prior list (stale-while-revalidate).
  // Backs BOTH subflows now: the manual typed-name lookup AND the AI name-pick
  // lookup fetch domains from the same godaddy `suggest` endpoint.
  // `domainsPaging` folds in the 0.4s skeleton flash the pager runs on every page
  // change (refresh / back / forward), so a local page swap reads as a real change.
  const domainsLoading = loadingManualDomains || domainsPaging;

  /* ── Mobile ────────────────────────────────────────────────────────────── */
  if (isMobile) {
    // Connect-domain sub-flow takes over the whole step body.
    if (connectStage) return <MobileConnect />;

    // Manual name input (sub-step 2a).
    if (step2Sub === 'own-name') {
      return (
        <MobileBody>
          <MobileHeading text="Enter your own name" tag="h1" />
          <MobileSubtext text="What would you like to name your iGaming website?" tag="paragraph" />
          <MobileList>
            <BrandNameInput placeholder={t('Enter your name')} value={typedName} onChange={setTypedName} />
          </MobileList>
          <DecideFastBtn type="button" onClick={() => setManualMode(false)}>
            <BoltIcon size={18} />
            <DecideFastLabel text="I want to decide fast" tag="span" />
          </DecideFastBtn>
        </MobileBody>
      );
    }

    // Domain sub-step (AI 1b or manual 2b — same screen, different source).
    if (step2Sub === 'domain' || step2Sub === 'own-domain') {
      const list = domains;
      const loading = domainsLoading;
      return (
        <MobileBody>
          <MobileHeading text="Choose a domain" tag="h1" />
          <MobileRefresh type="button" onClick={refreshDomains}>
            <RefreshIcon size={16} />
            <MobileRefreshLabel text="Refresh suggestions" tag="span" />
          </MobileRefresh>
          <MobileList>
            {loading ? (
              SKELETON_KEYS.map((k) => <Skeleton key={k} height={DOMAIN_ROW_HEIGHT} radius={16} />)
            ) : (
              <DomainRowsView domains={list} selectedDomain={selectedDomain} onSelect={setDomain} />
            )}
            <SuggestionPager nav={domainsNav} />
            {/* "I have my own domain" only on the manual domain sub-step
                ('own-domain'), not the AI domain sub-step ('domain'). */}
            {step2Sub === 'own-domain' ? <ConnectDomainRow onConnect={openConnectDomain} /> : null}
          </MobileList>
        </MobileBody>
      );
    }

    // AI name list (sub-step 1a).
    return (
      <MobileBody>
        <MobileHeading text="Choose a website name" tag="h1" />
        <MobileRefresh type="button" onClick={refreshNames}>
          <RefreshIcon size={16} />
          <MobileRefreshLabel text="Refresh suggestions" tag="span" />
        </MobileRefresh>
        <MobileList>
          {loadingNames ? (
            SKELETON_KEYS.map((k) => <Skeleton key={k} height={130} radius={22} />)
          ) : (
            <NameCardsView suggestions={names} selectedName={selectedName} onSelect={setName} />
          )}
        </MobileList>
        <DecideFastBtn type="button" onClick={() => setManualMode(true)}>
          <PencilIcon size={18} />
          <DecideFastLabel text="I have my own name" tag="span" />
        </DecideFastBtn>
      </MobileBody>
    );
  }

  /* ── Desktop connect-domain sub-flow ─────────────────────────────────────── */
  if (connectStage) return <DesktopConnect />;

  /* ── Desktop manual ──────────────────────────────────────────────────────── */
  if (manualMode) {
    return (
      <DesktopBody>
        <BrandNameInput
          label={t('Brand name')}
          placeholder={t('Type your own name here')}
          value={typedName}
          onChange={setTypedName}
        />
        {typedName.trim() ? (
          <Section>
            <SuggestionsHeader
              label={t('Lumio’s recommended domain for “{{name}}”')}
              placeholders={{ name: typedName.trim() }}
              onRefresh={refreshDomains}
            />
            <DomainList>
              {domainsLoading ? (
                SKELETON_KEYS.map((k) => <Skeleton key={k} height={DOMAIN_ROW_HEIGHT} radius={16} />)
              ) : (
                <DomainRowsView domains={domains} selectedDomain={selectedDomain} onSelect={setDomain} />
              )}
              <SuggestionPager nav={domainsNav} />
            </DomainList>
            <ConnectDomainRow onConnect={openConnectDomain} />
          </Section>
        ) : null}
      </DesktopBody>
    );
  }

  /* ── Desktop AI ────────────────────────────────────────────────────────── */
  return (
    <>
      <DesktopBody>
        <Section>
          <SuggestionsHeader
            label={t('Lumio’s suggestions for {{country}}')}
            placeholders={{ country: countryName }}
            onRefresh={refreshNames}
          />
          <NameGrid>
            {loadingNames ? (
              SKELETON_KEYS.map((k) => <Skeleton key={k} height={160} radius={22} />)
            ) : (
              <NameCardsView
                suggestions={names}
                selectedName={selectedName}
                onSelect={setName}
                onMore={setMoreSuggestion}
              />
            )}
          </NameGrid>
        </Section>

        {/* The recommended-domains box only appears once a name is picked (no
            empty placeholder before that), so the cards keep the space to breathe. */}
        {selectedName ? (
          <Section>
            <SuggestionsHeader
              label={t('Lumio’s recommended domains for “{{name}}”')}
              placeholders={{ name: selectedName }}
              onRefresh={refreshDomains}
            />
            <DomainList>
              {domainsLoading ? (
                SKELETON_KEYS.map((k) => <Skeleton key={k} height={DOMAIN_ROW_HEIGHT} radius={16} />)
              ) : (
                <DomainRowsView domains={domains} selectedDomain={selectedDomain} onSelect={setDomain} />
              )}
              <SuggestionPager nav={domainsNav} />
            </DomainList>
          </Section>
        ) : null}
      </DesktopBody>

      {moreSuggestion ? (
        <Sheet
          title={moreSuggestion.name}
          onClose={() => setMoreSuggestion(null)}
          footer={<Button label={t('Close')} variant="primary" onClick={() => setMoreSuggestion(null)} />}
        >
          <ModalBodyText text={moreSuggestion.why_it_works ?? ''} tag="paragraph" />
        </Sheet>
      ) : null}
    </>
  );
};

export default BrandStep;